/**
 * useCanSetEntityImage — the ONE permission gate for changing a VESSEL's
 * profile image (fleet-card 3-dot + SetImageModal + settings entry). FT-436.
 *
 * ⭐ Jonathan's rule: ONLY skipper + admin(fleet) + super_admin may CHANGE /
 * upload a photo or pick a placeholder. CREW = VIEW-ONLY.
 *
 * VESSELS-ONLY SLICE: this build renders vessel cards only — there is no
 * facility path. The gate mirrors the app's own skipper-gated "settings"
 * capability. In the RBAC matrix (src/hooks/usePermissions.js):
 *   - crew        → NO `settings` perms; wildcard is read-only  ⇒ update denied
 *   - skipper     → settings: ['read','update']                 ⇒ update allowed
 *   - fleet       → '*': ['*']                                   ⇒ allowed
 *   - super_admin → '*': ['*']                                   ⇒ allowed
 * i.e. `can('update','settings')` yields exactly {skipper, fleet, super_admin}.
 *
 * `can` ALSO layers a vessel-ASSIGNMENT check on top for non-fleet roles when a
 * targetVesselId is present — correct and desirable here (a skipper may only set
 * images for vessels they're assigned to, consistent with FT-435 assigned-only
 * cards). We therefore always pass the vessel id.
 *
 * Read/display is NEVER gated here — a photo an admin set DISPLAYS for everyone.
 *
 * NOTE: the `entityKind` argument is retained (always 'vessel' in this slice) so
 * the shared EntityProfilePhoto components keep one signature; a facility value
 * is never produced by any caller in this vessels-only build.
 */

import { usePermissions } from '../../../hooks/usePermissions';

/**
 * @param {'vessel'} entityKind  always 'vessel' in this slice (kept for a stable
 *                               shared component signature; no facility path).
 * @param {string} entityId      the vessel id.
 * @returns {boolean} true iff the current user may change this vessel's image.
 */
export function useCanSetEntityImage(entityKind, entityId) {
  const { can, isCrew } = usePermissions();

  // Belt-and-braces: crew are view-only, full stop. The `can` check below
  // already denies crew (no settings:update), but this makes the intent
  // impossible to regress by an accidental matrix edit.
  if (isCrew) return false;

  // Vessel: role gate + vessel-assignment gate (assigned-only, FT-435).
  return can('update', 'settings', entityId ? { vesselId: entityId } : {});
}

export default useCanSetEntityImage;
